/* eslint-disable prefer-destructuring */
/* eslint-disable no-use-before-define */
import React, {
  useRef, useEffect, Children,
} from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'react-jss';
import useStyle from './style';
import cx from '@/utils/cx';
import Swipe from '@/utils/swipe';


const ScrollableSpace = ({
  children,
  direction,
  scrollEffectedElementId,
  className,
  ...rest
}) => {
  const theme = useTheme();
  const childrenLength = Children.count(children);
  const classes = useStyle({ childrenLength, direction });
  const innerContainerRef = useRef();
  const outerContainerRef = useRef();
  const scrollEffectedElement = useRef(null);
  const scrollEffectedElementInitialTop = useRef(0);

  const { start } = theme.direction;
  const size = direction === 'x' ? 'offsetWidth' : 'offsetHeight';
  const transformFactor = (direction === 'x' ? theme.direction.transformFactor : 1);

  const createTranslateStr = (v) => `translate${direction.toUpperCase()}(${v}px)`;
  const readTranslateStr = (str) => (str ? parseInt(str.replace(/.+[(]/g, '').replace('px)', ''), 10) : 0);

  const keepInRange = (pos) => {
    let lastPos = pos;
    const range = [
      0,
      transformFactor
        * (outerContainerRef.current[size] - innerContainerRef.current[size]),
    ].sort();
    if (lastPos < range[0]) {
      lastPos = range[0];
    } else if (lastPos > range[1]) {
      lastPos = range[1];
    }
    return lastPos;
  };

  const swipe = Swipe();
  useEffect(() => {
    innerContainerRef.current.style[start] = 0;
    swipe.setElement(outerContainerRef.current);
    if (scrollEffectedElementId) {
      scrollEffectedElement.current = document.getElementById(scrollEffectedElementId);
    }

    swipe.on();
    return () => {
      swipe.off();
    };
  });

  let currentPos;
  swipe.wheel((dir) => {
    currentPos = readTranslateStr(innerContainerRef.current.style.transform);
    const newPos = keepInRange(currentPos + (dir * 60));
    innerContainerRef.current.style.transform = createTranslateStr(newPos);
  });
  swipe.beforeSwipe(() => {
    currentPos = readTranslateStr(innerContainerRef.current.style.transform);
    innerContainerRef.current.style.transitionDuration = '0s';
    if (scrollEffectedElement.current) {
      scrollEffectedElementInitialTop.current = scrollEffectedElement.current.scrollTop;
    }
  });
  swipe.whileSwipe((pos, diff) => {
    const newPos = currentPos + diff[direction];
    if (scrollEffectedElement.current) {
      scrollEffectedElement.current.scrollTop = scrollEffectedElementInitialTop.current - diff.y;
    }
    innerContainerRef.current.style.transform = createTranslateStr(newPos);
  });
  swipe.afterSwipe((pos, diff, time) => {
    innerContainerRef.current.style.transitionDuration = null;
    let lastPos = currentPos + diff[direction];

    lastPos += time < 250 ? (diff[direction] / (time / 100)) : 0;

    lastPos = keepInRange(lastPos);

    innerContainerRef.current.style.transform = createTranslateStr(lastPos);
  });


  return (
    <div
      className={cx(classes.scrollableSpace, className)}
      ref={outerContainerRef}
      {...rest}
    >
      <div className="inner" ref={innerContainerRef}>
        {children}
      </div>
    </div>
  );
};

ScrollableSpace.propTypes = {
  direction: PropTypes.oneOf(['x', 'y']),
  scrollEffectedElementId: PropTypes.string,
};

ScrollableSpace.defaultProps = {
  direction: 'x',
  scrollEffectedElementId: null,
};

export default ScrollableSpace;
